import type { AstrolabeId, KnockedOutBall, ScoredBall, ShotScore, TeamId } from "@scb/protocol";
import type { RulesConfig } from "./config";
import { chainLengthAt, cloneOccupancy, grandSlamTeam, type Occupancy } from "./occupancy";

/** 이번 라운드에서 한 팀이 각 Astrolabe에서 누적한 점수. Grand Slam "double" 계산에 쓰인다. */
export type PlateAccum = Record<AstrolabeId, number>;

export function emptyPlateAccum(): PlateAccum {
  return { 1: 0, 2: 0, 3: 0 };
}

export interface ShotCapture {
  ballId: number;
  /** 공의 소유 팀. 내 샷이 상대 공을 밀어 넣으면 그 점수는 상대 것이다. */
  team: TeamId;
  astrolabe: AstrolabeId;
  slot: number;
}

export interface ScoreShotArgs {
  config: RulesConfig;
  /** 샷 이전 점유 현황. 이 함수는 복제본을 쓰고 원본은 건드리지 않는다. */
  before: Occupancy;
  /** 시뮬레이션 이벤트 순서대로의 포획 목록 (착지 순서가 점수에 영향을 준다) */
  captures: readonly ShotCapture[];
  /** [team0, team1]의 라운드 누적 판별 점수 */
  plateAccum: readonly [PlateAccum, PlateAccum];
}

export interface ScoreShotResult {
  scores: [ShotScore, ShotScore];
  plateAccum: [PlateAccum, PlateAccum];
  /** 모든 포획을 반영한 최종 점유 현황 */
  occupancy: Occupancy;
}

/**
 * 한 번의 샷으로 발생한 모든 포획의 점수를 계산한다.
 *
 * 규칙 (docs/01-game-rules.md §3):
 *   기본점     — Position 1/2/3 = 1/2/5점
 *   Chaining   — 같은 판에서 인접한 같은 색 공, 추가 1개당 +1/+2/+5
 *   Grand Slam — 판을 자기 색으로 가득 채우면 그 판 누적 점수만큼 추가 (총점 2배)
 *   마지막 공이 Chaining과 Grand Slam을 동시에 완성하면 둘 다 적용
 *
 * Chaining은 **공이 착지하는 시점**의 배치로 계산한다. 한 샷에서 여러 공이
 * 연달아 들어가면 나중에 들어간 공일수록 연쇄가 길어진다.
 */
export function scoreShot(args: ScoreShotArgs): ScoreShotResult {
  const { config, captures } = args;

  const occupancy = cloneOccupancy(args.before);
  const plateAccum: [PlateAccum, PlateAccum] = [
    { ...args.plateAccum[0] },
    { ...args.plateAccum[1] },
  ];
  const balls: [ScoredBall[], ScoredBall[]] = [[], []];
  const grandSlams: [AstrolabeId[], AstrolabeId[]] = [[], []];
  const totals: [number, number] = [0, 0];

  for (const cap of captures) {
    const row = occupancy[cap.astrolabe];
    if (cap.slot < 0 || cap.slot >= row.length) continue;

    const alreadySlam = grandSlamTeam(occupancy, cap.astrolabe) === cap.team;
    row[cap.slot] = cap.team;

    const base = config.basePoints[cap.astrolabe];
    const chainLength = chainLengthAt(occupancy, cap.astrolabe, cap.slot);
    const chainBonus = Math.max(0, chainLength - 1) * config.chainBonus[cap.astrolabe];

    let gained = base + chainBonus;
    plateAccum[cap.team][cap.astrolabe] += gained;

    const scored: ScoredBall = {
      ballId: cap.ballId,
      astrolabe: cap.astrolabe,
      slot: cap.slot,
      base,
      chainBonus,
      chainLength,
      grandSlamBonus: 0,
      total: gained,
    };

    // 이 공으로 판이 "새로" 가득 찼는가?
    if (!alreadySlam && grandSlamTeam(occupancy, cap.astrolabe) === cap.team) {
      const bonus =
        config.grandSlamMode === "double"
          ? plateAccum[cap.team][cap.astrolabe]
          : config.grandSlamFlat[cap.astrolabe];

      scored.grandSlamBonus = bonus;
      scored.total += bonus;
      gained += bonus;
      plateAccum[cap.team][cap.astrolabe] += bonus;
      grandSlams[cap.team].push(cap.astrolabe);
    }

    totals[cap.team] += gained;
    balls[cap.team].push(scored);
  }

  return {
    scores: [
      { team: 0, balls: balls[0], total: totals[0], grandSlams: grandSlams[0] },
      { team: 1, balls: balls[1], total: totals[1], grandSlams: grandSlams[1] },
    ],
    plateAccum,
    occupancy,
  };
}

/** `knockedOutBalls`가 보는 최소 형태 — SimBall이 이 조건을 만족한다. */
export interface BallAfterShot {
  id: number;
  team: TeamId;
  /** "captured"면 여전히 안착 중 (재안착 포함) */
  state: string;
}

/**
 * 이 샷으로 칸에서 빠져나간 공들과 차감액을 판정한다 (RulesConfig.knockoutPenalty).
 *
 * "빠져나갔다" = 샷 이전에 안착해 있었는데(`capturedBefore`) 샷이 끝난 뒤에는
 * 안착 상태가 아니다. 같은 샷에서 다른 칸에 **재안착**한 공은 여전히 안착
 * 상태라 여기 걸리지 않는다 — 재안착은 점수도 주지 않으므로(config 주석 참고)
 * 차감도 하지 않는 게 대칭이다.
 *
 * 차감액은 빠진 판의 **기본 점수**다. 그 공이 실제로 벌었던 연쇄·만관 보너스는
 * 추적하지 않는다 — 규칙이 "빠지면 그 판 점수만큼 잃는다"로 한 줄에 설명돼야
 * 게임 중에 계산이 선다. 점수를 0 밑으로 내리는 것은 호출자가 막는다.
 */
export function knockedOutBalls(
  config: RulesConfig,
  capturedBefore: ReadonlyMap<number, AstrolabeId>,
  ballsAfter: readonly BallAfterShot[],
): KnockedOutBall[] {
  const out: KnockedOutBall[] = [];
  for (const ball of ballsAfter) {
    const astrolabe = capturedBefore.get(ball.id);
    if (astrolabe === undefined || ball.state === "captured") continue;
    out.push({
      ballId: ball.id,
      team: ball.team,
      astrolabe,
      penalty: config.knockoutPenalty ? config.basePoints[astrolabe] : 0,
    });
  }
  return out;
}
